/**
 * Where each node referenced in the Director composer came from, for
 * `project_prompt_submitted` (analytics doc follow-up row 7).
 *
 * The composer has one editor per page. A reference is recorded when its chip
 * is inserted: from a composer upload, a typed @mention, or the node toolbar's
 * Add to chat. The last insert of a node wins. An Add to chat insert also
 * carries the `interaction_id` of the toolbar click that requested it.
 */
export type ChatReferenceSource = 'upload' | 'typed_mention' | 'add_to_chat'

const MAX_TRACKED = 500
const origins = new Map<string, { source: ChatReferenceSource; interactionId?: string }>()
let pendingToolbarClick: { nodeId: string; interactionId: string } | null = null

/** The node toolbar's Add to chat was clicked; its chip is inserted shortly after. */
export function noteToolbarAddToChat(nodeId: string, interactionId: string): void {
  pendingToolbarClick = { nodeId, interactionId }
}

/** A reference chip for `nodeId` was inserted into the composer. */
export function noteChatReference(nodeId: string, source: ChatReferenceSource): void {
  let interactionId: string | undefined
  if (source === 'add_to_chat' && pendingToolbarClick?.nodeId === nodeId) {
    interactionId = pendingToolbarClick.interactionId
    pendingToolbarClick = null
  }
  origins.delete(nodeId)
  origins.set(nodeId, { source, interactionId })
  if (origins.size > MAX_TRACKED) {
    const oldest = origins.keys().next().value
    if (oldest !== undefined) origins.delete(oldest)
  }
}

export interface ReferenceOriginProps {
  reference_from_upload: boolean
  reference_from_mention: boolean
  reference_from_add_to_chat: boolean
  /** The toolbar click behind the most recent Add to chat reference, if any. */
  interaction_id?: string
}

export function referenceOriginProps(nodeIds: readonly string[] | undefined): ReferenceOriginProps {
  const props: ReferenceOriginProps = {
    reference_from_upload: false,
    reference_from_mention: false,
    reference_from_add_to_chat: false,
  }
  for (const nodeId of nodeIds ?? []) {
    const origin = origins.get(nodeId)
    if (!origin) continue
    if (origin.source === 'upload') props.reference_from_upload = true
    else if (origin.source === 'typed_mention') props.reference_from_mention = true
    else {
      props.reference_from_add_to_chat = true
      if (origin.interactionId) props.interaction_id = origin.interactionId
    }
  }
  return props
}

export function _resetChatReferenceOriginForTest(): void {
  origins.clear()
  pendingToolbarClick = null
}
